import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ResumeUpload from './Auth/ResumeUpload';
import './CompareAnalysisModal.css';

interface CompareAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
  onLoginRequired?: (action: string) => void;
  mode?: 'compare-salary' | 'upload-resume';
}

const CompareAnalysisModal: React.FC<CompareAnalysisModalProps> = ({
  isOpen,
  onClose,
  darkMode,
  onLoginRequired,
  mode = 'compare-salary'
}) => {
  const { isAuthenticated, user } = useAuth();
  const [showDevBanner, setShowDevBanner] = useState(false);

  if (!isOpen) return null;

  const handleUploadComplete = () => {
    // Show development banner after CV upload for salary comparison
    // For resume upload mode, just close the modal
    if (mode === 'upload-resume') {
      alert('Resume uploaded successfully! You will now receive personalized job recommendations.');
      onClose();
    } else {
      setShowDevBanner(true);
    }
  };

  // Handler for using default search
  const handleDefaultSearch = () => {
    onClose();
    // Trigger the comparison search
    const event = new CustomEvent('triggerComparisonSearch');
    window.dispatchEvent(event);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className={`modal-content compare-analysis-modal ${darkMode ? 'dark' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>

        <div className="modal-header">
          <h2>{mode === 'upload-resume' ? 'Get Personalized Job Recommendations' : 'Get Accurate Salary Analysis'}</h2>
          <p>{mode === 'upload-resume' 
            ? 'Upload your resume to find jobs that match your skills and experience' 
            : 'Upload your CV to analyze your salary potential in your current location'}</p>
        </div>

        {!isAuthenticated ? (
          <div className="auth-required">
            <div className="auth-icon">
              <span className="material-icons">person_outline</span>
            </div>
            <h3>Want Personalized {mode === 'upload-resume' ? 'Job Matches' : 'Salary Analysis'}?</h3>
            <p>Sign in to {mode === 'upload-resume' 
              ? 'upload your resume and get job recommendations matched to your skills' 
              : 'upload your CV and analyze your salary potential based on your experience'}</p>
            <div className="auth-actions">
              <button 
                className="signin-btn"
                onClick={() => {
                  onClose();
                  if (onLoginRequired) {
                    onLoginRequired(mode === 'upload-resume' ? 'upload-resume' : 'compare-salary');
                  }
                }}
              >
                <span className="material-icons">login</span>
                Sign In for Personalized Results
              </button>
              <div className="divider-with-text">
                <span>OR</span>
              </div>
              <button 
                className="default-search-btn"
                onClick={handleDefaultSearch}
              >
                <span className="material-icons">search</span>
                Continue with Default Search
              </button>
              <p className="default-search-note">
                {mode === 'upload-resume' 
                  ? 'Browse jobs without personalized recommendations'
                  : 'Compare salaries using basic filters without skill analysis'}
              </p>
            </div>
          </div>
        ) : (
          <div className="analysis-content">
            {!user?.resume && !showDevBanner ? (
              <div className="upload-section">
                <ResumeUpload 
                  darkMode={darkMode}
                  onUploadComplete={handleUploadComplete}
                  hideHeader={true}
                />
                <div className="skip-option">
                  <button 
                    className="skip-btn"
                    onClick={handleDefaultSearch}
                  >
                    <span className="material-icons">search</span>
                    Use Default Search Instead
                  </button>
                  <p className="skip-text">
                    {mode === 'upload-resume' 
                      ? "Continue browsing jobs without skill-based matching"
                      : "Compare salaries without analyzing your specific skills and experience"
                    }
                  </p>
                </div>
              </div>
            ) : (
              <div className="dev-banner-container">
                {user?.resume && !showDevBanner && (
                  <div className="existing-cv">
                    <div className="cv-info">
                      <span className="material-icons">description</span>
                      <div>
                        <h4>CV Already Uploaded</h4>
                        <p>Would you like to use your existing CV for analysis?</p>
                      </div>
                    </div>
                    <div className="cv-actions">
                      <button 
                        className="use-existing-btn"
                        onClick={() => setShowDevBanner(true)}
                      >
                        Use Existing CV
                      </button>
                      <button 
                        className="upload-new-btn"
                        onClick={() => setShowDevBanner(false)}
                      >
                        Upload New CV
                      </button>
                    </div>
                  </div>
                )}

                {showDevBanner && (
                  <div className="development-banner">
                    <div className="banner-icon">
                      <span className="material-icons">construction</span>
                    </div>
                    <h3>{mode === 'upload-resume' ? 'AI Job Matching Coming Soon!' : 'AI Salary Analysis Coming Soon!'}</h3>
                    <p>{mode === 'upload-resume' 
                      ? 'Our AI-powered job matching system is currently in development.'
                      : 'Our AI-powered salary analysis for your location is currently in development.'
                    }</p>
                    <div className="feature-preview">
                      <h4>What to expect:</h4>
                      <ul>
                        {mode === 'upload-resume' ? (
                          <>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Jobs matched to your specific skills and experience
                            </li>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Compatibility score for each job listing
                            </li>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Personalized job recommendations
                            </li>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Skills gap analysis for dream positions
                            </li>
                          </>
                        ) : (
                          <>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Salary potential in your current location
                            </li>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Skill-based salary analysis
                            </li>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Market positioning for your experience level
                            </li>
                            <li>
                              <span className="material-icons">check_circle</span>
                              Location-specific compensation insights
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                    <div className="banner-footer">
                      <p>We're working hard to bring you this feature. Stay tuned!</p>
                      <button className="notify-btn">
                        <span className="material-icons">notifications</span>
                        Notify Me When Ready
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareAnalysisModal;